import { useState, useEffect, useRef, useMemo, createRef } from 'react';
import { TagCloud, Tag, RendererFunction } from 'react-tagcloud';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Tooltip } from '@mui/material';
import { getWordCloudColor } from './analyticsColors';
import { ANALYTICS_BODY_FONT_FAMILY } from './chartTypography';

import './WordCloud.css';

const useStyles = makeStyles(() => ({
  h5: {
    fontSize: '150%',
  },
  wordCloudContainer: {
    width: '100%',
    height: '100%',
    minHeight: '280px',
    padding: '4px',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
  },
  cloudWrapper: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  cloudInner: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    padding: '28px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

interface WordCloudProps {
  chartData: {
    y: string[];
    x: number[];
  }[];
  title: string;
  showTitle?: boolean;
  paletteVariant?: 'default' | 'custom';
  onAnimationComplete?: () => void; // Add callback prop for when animations complete
}

export function WordCloud({
  chartData,
  title,
  showTitle = true,
  paletteVariant = 'default',
  onAnimationComplete = () => {},
}: Readonly<WordCloudProps>) {
  const classes = useStyles();
  const wordCloudRef = useRef<HTMLDivElement | null>(null);
  const safeAreaRef = useRef<HTMLDivElement | null>(null);
  const animationCountRef = useRef<number>(0);
  const MIN_WORD_CLOUD_FONT_SIZE = 14;
  const MAX_WORD_CLOUD_FONT_SIZE = 54;
  const [minSize, setMinSize] = useState<number>(30);
  const [maxSize, setMaxSize] = useState<number>(70);

  const words: Tag[] = useMemo(() => {
    const fourStepScale: string[] = [
      getWordCloudColor(0, { variant: paletteVariant }),
      getWordCloudColor(3, { variant: paletteVariant }),
      getWordCloudColor(1, { variant: paletteVariant }),
      getWordCloudColor(4, { variant: paletteVariant }),
    ];

    const data_y = chartData[0].y.map((i: string) => i.replace(/<br>/g, ' '));
    const data_x = chartData[0].x;
    const numericCounts = data_x.map((count) => Number(count) || 0);
    const minCount = Math.min(...numericCounts);
    const maxCount = Math.max(...numericCounts);

    const newWords = data_y.map((text, i) => {
      const count = numericCounts[i];
      const normalizedCount = maxCount === minCount ? 0.66 : (count - minCount) / (maxCount - minCount);
      const colorBucketIndex = Math.min(3, Math.floor(normalizedCount * 4));

      return {
        value: text,
        count,
        color: fourStepScale[colorBucketIndex],
        index: i,
      };
    });

    return newWords.slice(0, 30); // Max 30 words
  }, [chartData, paletteVariant]);

  const tagRef = useRef<any>(words.map(() => createRef<HTMLSpanElement>()));

  const customRenderer: RendererFunction = (tag, size, color) => {
    return (
      <Tooltip title={`Count: ${tag.count}`} key={tag.value}>
        <span
          ref={tag.index !== undefined ? tagRef.current[tag.index as keyof typeof tagRef.current] : undefined}
          className="animated-word"
          style={{
            fontSize: `${size}px`,
            margin: '2px 7px',
            color: color,
            fontFamily: ANALYTICS_BODY_FONT_FAMILY,
            lineHeight: 1.2,
          }}
          onAnimationEnd={() => handleAnimationEnd(tag.index as keyof typeof tagRef.current)}
        >
          {tag.value}
        </span>
      </Tooltip>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (safeAreaRef.current) {
        const { offsetWidth: width, offsetHeight: height } = safeAreaRef.current;

        const shortestSide = Math.min(width, height);
        const nextMaxFontSize = Math.min(MAX_WORD_CLOUD_FONT_SIZE, Math.max(22, shortestSide * 0.105));
        const nextMinFontSize = Math.min(nextMaxFontSize - 6, Math.max(MIN_WORD_CLOUD_FONT_SIZE, shortestSide * 0.055));

        setMinSize(nextMinFontSize);
        setMaxSize(nextMaxFontSize);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Reset count at the beginning of a new animation cycle
    animationCountRef.current = 0;
  }, [words]);

  // each tag animates separately. wait for all of them to end before firing a callback
  const handleAnimationEnd = (index: keyof typeof tagRef.current) => {
    animationCountRef.current += 1;

    // After an animation is finished, we want to remove the css class that triggers said animation.
    // This is due to html2canvas not working properly if the captured component has an "animation" css.
    tagRef.current[index]?.current?.classList.remove('animated-word');
    tagRef.current[index]?.current?.classList.add('animated-word-complete');

    // When all animations have completed, call the parent callback
    if (animationCountRef.current >= words.length && onAnimationComplete) {
      onAnimationComplete();
    }
  };

  return (
    <>
      {showTitle && (
        <Typography className={classes.h5} variant="h5" align="center">
          {title}
        </Typography>
      )}
      <div className={classes.wordCloudContainer} ref={wordCloudRef}>
        <div className={classes.cloudWrapper}>
          <div className={classes.cloudInner} ref={safeAreaRef}>
            <TagCloud minSize={minSize} maxSize={maxSize} tags={words} renderer={customRenderer} />
          </div>
        </div>
      </div>
    </>
  );
}
